import { z } from 'zod';
import { DAILY_BONUS_GAP_MS, MAX_TIER, levelProgress, tierForLevel, } from './levels.js';
export const FORMAT_KEYS = ['quick', 'classic300', 'classic500', 'money'];
/**
 * The format a match was played under, flattened.
 *
 * `matches.format` stores only the kind, so a stored classic match has to be
 * reunited with `rules.format.target` before it can be scored — see
 * `rebuild-profiles.ts`. An unrecognised shape reads as `quick`, the cheapest
 * key, so a corrupt row underpays rather than mints.
 */
export function formatKey(f) {
    if (f.kind === 'classic')
        return f.target === 300 ? 'classic300' : 'classic500';
    return f.kind === 'money' ? 'money' : 'quick';
}
/** Is this string one of the four keys? Guards values coming back out of the DB. */
export function isFormatKey(value) {
    return typeof value === 'string' && FORMAT_KEYS.includes(value);
}
/**
 * Points-to-XP rate per format. A quick match's winning team lands ~250–450
 * points, a classic-300 ~300, a classic-500 ~500, a Money Hand ~100–250 — so the
 * scales are set to bring all four into the same XP band rather than to reward
 * the format that happens to print the biggest number.
 */
export const MATCH_XP_SCALE = {
    quick: 0.45,
    classic300: 0.9,
    classic500: 0.55,
    money: 0.5,
};
/** Ceiling on a WINNER's base + nils, before the flat `WIN_BONUS`. Per format. */
export const MATCH_XP_CAP = {
    quick: 260,
    classic300: 320,
    classic500: 420,
    money: 160,
};
/**
 * Ceiling on a LOSER's base + nils, every format. One number, not four, because
 * its job is not to price a format: it is the ceiling that keeps the whole loser
 * band under `WIN_BASE_FLOOR + WIN_BONUS`, so a loss can never out-earn a win.
 */
export const LOSS_CAP = 150;
/** Flat reward for winning, added AFTER the cap so it is never eaten by it. */
export const WIN_BONUS = 100;
/** A win's base never reads lower than this, however thin the score was. */
export const WIN_BASE_FLOOR = 100;
/** Per nil THIS seat bid and made. Blind nils count as nils; partners' do not. */
export const NIL_MADE_BONUS = 50;
/**
 * XP for one seat of one finished match. Pure, total, and the only place the
 * arithmetic lives — the room, the profile route and the rebuild script all call
 * this rather than each keeping a copy.
 */
export function xpForMatchSeat(i) {
    const k = typeof i.format === 'string' ? i.format : formatKey(i.format);
    const scaled = Math.round(Math.max(0, i.teamTotal || 0) * MATCH_XP_SCALE[k]);
    const baseRaw = i.won ? Math.max(scaled, WIN_BASE_FLOOR) : scaled;
    const cap = i.won ? MATCH_XP_CAP[k] : LOSS_CAP;
    const nilsMade = Math.max(0, Math.floor(i.nilsMade || 0));
    const earned = Math.min(cap, baseRaw + nilsMade * NIL_MADE_BONUS);
    // The cap bites the NILS first and the base only once nothing is left: a
    // winner whose scaled base already exceeds the cap should read as a big base
    // with no nil row, not as a small base padded by nils it did not earn.
    const base = Math.min(earned, baseRaw);
    const win = i.won ? WIN_BONUS : 0;
    return { base, nils: earned - base, win, total: earned + win };
}
/**
 * The metals. One per 5 levels (`tierForLevel`), six bands: 1–4, 5–9, 10–14,
 * 15–19, 20–24, 25+. Index with the TIER, not the level.
 */
export const TIER_NAMES = [
    'Bronze',
    'Silver',
    'Gold',
    'Platinum',
    'Diamond',
    'Black Spade',
];
/** The metal a level wears. Clamped, so any junk level still names a tier. */
export function tierName(level) {
    return TIER_NAMES[Math.min(tierForLevel(level), MAX_TIER)];
}
/**
 * Whether a player returning at `at` has been away long enough for the daily
 * grant. `null`/unparseable `lastGameAt` means they have never finished a match,
 * which is `first_game` rather than `daily`.
 */
export function pendingBonusFor(lastGameAt, at = Date.now()) {
    if (lastGameAt == null)
        return 'first_game';
    const last = lastGameAt instanceof Date ? lastGameAt.getTime() : Date.parse(lastGameAt);
    if (!Number.isFinite(last))
        return 'first_game';
    const now = at instanceof Date ? at.getTime() : at;
    return now - last >= DAILY_BONUS_GAP_MS ? 'daily' : null;
}
/**
 * Which welcome a seat is OWED at match start, composed from the XP ledger and
 * the gap.
 *
 * `hadFirstGrant` is "does this player have a `first_game` row in `xp_awards`" —
 * and it, not the existence of a `player_profiles` row, is what decides the first
 * grant. THE LEDGER IS THE AUTHORITY, for a reason that only shows up on the
 * unhappy path: a profile row is written by every finished match, but the grant
 * is not. A first-timer whose frozen decision went missing (a read that failed, a
 * server restarted mid-match) is paid nothing, correctly — and gets a profile row
 * anyway. Reading first-ness off that row would mean the very next match sees
 * "not new", so the grant they were never paid could never be offered again.
 *
 *   no first_game row → 'first_game'   (however many rows say otherwise)
 *   gap >= 12h        → 'daily'
 *   otherwise         → null
 */
export function bonusOwedFrom(hadFirstGrant, lastGameAt, at = Date.now()) {
    if (!hadFirstGrant)
        return 'first_game';
    const kind = pendingBonusFor(lastGameAt, at);
    return kind === 'first_game' ? null : kind;
}
/**
 * What one seat is actually PAID: exactly the grant the room froze and showed,
 * and NOTHING otherwise.
 *
 * SHOWN == PAID, as an invariant rather than an intention — which is why this
 * function cannot reach the gap rule at all. The room decides the grant once, at
 * match start, and the end screen renders a ledger row from it; by the time this
 * runs the grant is a PROMISE, and the only question left is whether it can be
 * honoured, never whether it was right. Re-deciding from `last_game_at` here is
 * the classic bug: a seat 11h50m past its last match is shown no daily grant,
 * finishes twenty minutes later with a >=12h gap, and is quietly paid 100 XP the
 * end screen never showed.
 *
 * So `undefined` (no decision — a failed read, a server restarted mid-match) and
 * `null` (a decision of "nothing owed") both pay nothing. Nothing is permanently
 * lost by the silent case: no `xp_awards` row is written, and first-ness is read
 * from that ledger rather than from the profile row this match writes anyway — so
 * the NEXT match, whose freeze will land, offers the same grant.
 *
 * `hadFirst` still overrides everything: you can only be new once, so a stale or
 * replayed 'first_game' promise to a player who already has one is refused.
 */
export function welcomeGrantFor(frozen, hadFirst) {
    if (frozen == null)
        return null;
    return frozen === 'first_game' && hadFirst ? null : frozen;
}
// ─── Wire shapes ─────────────────────────────────────────────────────────────
export const FormatKeySchema = z.enum(['quick', 'classic300', 'classic500', 'money']);
export const BonusKindSchema = z.enum(['first_game', 'daily']);
/** One row of a profile's match history. Timestamps are ISO strings on the wire. */
export const ProfileMatchSchema = z.object({
    matchId: z.string(),
    endedAt: z.string(),
    format: FormatKeySchema,
    /** `null` for a match whose seats were never closed out (abandoned, crashed). */
    won: z.boolean().nullable(),
    teamTotal: z.number().int(),
    opponentTotal: z.number().int(),
    /** The `match` XP banked for this seat — grants are not included. */
    xp: z.number().int(),
    seat: z.number().int().min(0).max(3),
});
/**
 * Everything the Profile sheet renders. Level and tier are DERIVED from `xp`
 * here and on the server (`levelProgress`), never read from the cached
 * `player_profiles.level` column, so a retune shows up immediately.
 */
export const ProfileViewSchema = z.object({
    userId: z.string(),
    displayName: z.string().nullable(),
    avatar: z.string().nullable(),
    level: z.number().int().min(1),
    xp: z.number().int().min(0),
    intoLevel: z.number().int().min(0),
    required: z.number().int().min(1),
    fraction: z.number().min(0).max(1),
    nextLevel: z.number().int().min(2),
    tier: z.number().int().min(0),
    tierName: z.string(),
    gamesPlayed: z.number().int().min(0),
    wins: z.number().int().min(0),
    /** `null` until they have finished a match — never 0/0. */
    winrate: z.number().min(0).max(1).nullable(),
    firstGameAt: z.string().nullable(),
    lastGameAt: z.string().nullable(),
    matches: z.array(ProfileMatchSchema),
});
/**
 * The one-shot `progress` message a seat gets at match end.
 *
 * It carries a BEFORE/AFTER snapshot rather than a running total, which is what
 * keeps a reload honest: the end screen animates `xpBefore → xpAfter` from this
 * message alone, and no XP lives in the room schema to go stale.
 */
export const ProgressMessageSchema = z.object({
    matchId: z.string(),
    base: z.number().int().min(0),
    nils: z.number().int().min(0),
    win: z.number().int().min(0),
    /** The welcome grant actually paid, 0 when none was. */
    bonus: z.number().int().min(0),
    bonusKind: BonusKindSchema.nullable(),
    /** base + nils + win + bonus. */
    total: z.number().int().min(0),
    xpBefore: z.number().int().min(0),
    xpAfter: z.number().int().min(0),
    levelBefore: z.number().int().min(1),
    levelAfter: z.number().int().min(1),
});
/**
 * Compose the wire view from a stored profile and its history. The single place
 * level, tier and winrate are derived, so the route and any test agree by
 * construction.
 */
export function profileView(summary, matches = []) {
    const xp = Math.max(0, Math.floor(summary.xpTotal || 0));
    const p = levelProgress(xp);
    const tier = tierForLevel(p.level);
    return {
        userId: summary.userId,
        displayName: summary.displayName,
        avatar: summary.avatar,
        level: p.level,
        xp,
        intoLevel: p.intoLevel,
        required: p.required,
        fraction: p.fraction,
        nextLevel: p.nextLevel,
        tier,
        tierName: tierName(p.level),
        gamesPlayed: summary.gamesPlayed,
        wins: summary.wins,
        winrate: summary.gamesPlayed > 0 ? summary.wins / summary.gamesPlayed : null,
        firstGameAt: summary.firstGameAt,
        lastGameAt: summary.lastGameAt,
        matches,
    };
}
//# sourceMappingURL=progression.js.map